/**
 * M4.V3 — the Gantt chart, and (with `chrome`) M4.V5's Timeline
 * (`specs/M4-V3-gantt.md`, `specs/M4-V5-timeline.md`).
 *
 * monday's two kinds are one component: same axis, same rows, same bars, same settings; the
 * Timeline differs by a 232 px side panel instead of 416 and no *Baseline* button
 * `[cap:views/INDEX.md]`. Writing a second renderer would invent a difference monday does not
 * have, so this takes a `chrome` prop and `TimelineView.tsx` is four lines.
 *
 * The read is B4's: `BoardDataSource.load()` with B6's filter and, when `group_by_id` names a
 * column, B4 §8.8's `groupBy`. Every write is one `setCellValues` on the bar's own date column,
 * so the events outbox, the activity row, automations and realtime all fire with no Gantt-side
 * server code (`CLAUDE.md` §5).
 */
import {
  addDays,
  createTimelineAxis,
  dayNameOf,
  fitZoom,
  GANTT_METRICS,
  isUngroupedToken,
  TIMELINE_ZOOMS,
  type GanttViewSettings,
  type TimelineZoom,
} from '@elizmonday/shared';
import { PageEmptyState, PageSkeleton } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { BoardDataSource } from '../../board-view/data/types';
import { useViewData } from '../shared/use-view-data';
import { useViewSettings, type ViewSettingsOverride } from '../shared/use-view-settings';
import { ViewToolbar, type SettingsField } from '../shared/ViewToolbar';
import { BoardViewToolbar, useViewQueryControls } from '../shared/BoardViewToolbar';
import {
  buildGanttGroups,
  dateColumnsOf,
  dependencyColumnOf,
  dependencyEdges,
  dependsOnOf,
  dragValue,
  flattenGanttRows,
  ganttQueryGroupBy,
  ganttRange,
  mergeIntoOneGroup,
} from './rows';
import './timeline.css';

/** The two chrome differences between monday's Gantt and its Timeline (`[V5§1]`). */
export interface TimelineChrome {
  readonly sidePanelWidth: number;
  readonly showBaseline: boolean;
  readonly sidePanelDates: boolean;
}

export const GANTT_CHROME: TimelineChrome = { sidePanelWidth: 416, showBaseline: true, sidePanelDates: true };
export const TIMELINE_CHROME: TimelineChrome = { sidePanelWidth: 232, showBaseline: false, sidePanelDates: false };

export interface GanttViewProps {
  boardId: string;
  viewId: string;
  source: BoardDataSource;
  kind?: 'gantt' | 'timeline';
  chrome?: TimelineChrome;
  settingsOverride?: ViewSettingsOverride<GanttViewSettings>;
  /** Frozen "today" for the tests; the product passes nothing. */
  today?: string;
}

/** How many rows to page in before drawing; the axis needs the data's extent (`[V3§5]`). */
const PAGE_CEILING = 400;

export function GanttView({
  boardId,
  viewId,
  source,
  kind = 'gantt',
  chrome = kind === 'timeline' ? TIMELINE_CHROME : GANTT_CHROME,
  settingsOverride,
  today: frozenToday,
}: GanttViewProps) {
  const handle = useViewSettings<GanttViewSettings>(boardId, viewId, kind, settingsOverride);
  const settings = handle.settings;
  const navigate = useNavigate();
  /**
   * **P-147.** The zoom is a **setting**, not component state: `GanttViewSettings.zoom` is
   * imported from monday's `settings_str`, is what the view opens on, and was what every
   * reload came back to, because the zoom select, the two steppers and *Auto Fit* all wrote
   * this `useState` and never the view row. It stays here as the optimistic overlay — the pick
   * has to repaint the axis before `views.update` answers — and `pickZoom` is the only writer.
   * Whether monday stores it on the view or per user is q532; the view row is the conservative
   * reading, because that is where monday's own `settings_str` carries it.
   */
  const [zoom, setZoom] = useState<TimelineZoom | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(1000);

  const today = frozenToday ?? new Date().toISOString().slice(0, 10);

  /** P-025: the board toolbar, on the timeline family too — search, person, filter, sort. */
  const toolbar = useViewQueryControls();

  const data = useViewData({
    source,
    boardId,
    viewId,
    state: (() => {
      const groupBy = ganttQueryGroupBy(settings);
      return groupBy === undefined ? toolbar.state : { ...toolbar.state, groupBy: groupBy as never };
    })(),
  });

  useEffect(() => {
    data.loadAll(PAGE_CEILING);
  }, [data]);

  /**
   * Open on the data, not on the padded left edge. The range is snapped outward to whole
   * months, so at Days zoom (322 px/day) a board whose items start on the 20th would otherwise
   * open on 6,000 px of empty February — which is exactly what `views-timeline.spec.ts` caught.
   * Re-runs only when the range or the scale changes, so a user's own scroll is never fought.
   */
  const scrolledTo = useRef<string | null>(null);

  useEffect(() => {
    const element = bodyRef.current;
    if (element === null) return undefined;
    const measure = () => setViewportWidth(Math.max(200, element.clientWidth - chrome.sidePanelWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [chrome.sidePanelWidth]);

  const columns = data.payload?.columns ?? [];
  const people = data.payload?.people ?? [];

  const groups = useMemo(() => {
    const built = buildGanttGroups({
      settings,
      groups: data.payload?.groups ?? [],
      rowsOf: data.rowsOf,
      columns,
      people,
    });
    // P-149: the merged bucket honours `collapsedGroupKeys` like any real group id.
    return isUngroupedToken(settings.groupBy) ? mergeIntoOneGroup(built, settings) : built;
  }, [settings, data.payload, data.rowsOf, columns, people]);

  const range = useMemo(() => ganttRange(groups, today), [groups, today]);
  const effectiveZoom = zoom ?? settings.zoom;

  const axis = useMemo(
    () => createTimelineAxis({ zoom: effectiveZoom, from: range.from, to: range.to }),
    [effectiveZoom, range.from, range.to],
  );

  const rows = useMemo(
    () => flattenGanttRows(groups, { groupHeaderHeight: GANTT_METRICS.groupHeaderHeight, rowHeight: GANTT_METRICS.rowHeight }),
    [groups],
  );
  const edges = useMemo(() => (settings.showDependencies ? dependencyEdges(rows) : []), [rows, settings.showDependencies]);

  /** Row tops, so the arrow overlay and the lanes agree without a second layout pass. */
  const tops = useMemo(() => {
    const map = new Map<string, number>();
    let y = 0;
    for (const row of rows) {
      if (row.kind === 'bar') map.set(row.bar.itemId, y);
      y += row.height;
    }
    return map;
  }, [rows]);

  const write = useCallback(
    (itemId: string, columnId: string, value: Record<string, unknown>) => {
      void source
        .setCellValues({ boardId: boardId as never, writes: [{ itemId: itemId as never, columnId: columnId as never, value }] })
        .then(() => {
          setError(null);
          data.refresh();
        })
        .catch((reason: unknown) => {
          setError(reason instanceof Error ? reason.message : 'The bar could not be moved');
          data.refresh();
        });
    },
    [source, boardId, data],
  );

  /**
   * **P-141.** The board's `dependency` column — the one a link writes. A board without one
   * draws no arrows and offers no ports: monday has no dependency to store either.
   */
  const dependencyColumn = useMemo(() => dependencyColumnOf(columns), [columns]);

  /** P-141: the rows container, so a link drag can be drawn in canvas coordinates. */
  const rowsRef = useRef<HTMLDivElement | null>(null);

  /** P-147: every zoom control goes through this — paint now, store on the view row. */
  const pickZoom = useCallback(
    (next: TimelineZoom) => {
      setZoom(next);
      handle.save({ zoom: next });
    },
    [handle],
  );

  const drag = useTimelineDrag({
    pxPerDay: axis.pxPerDay,
    onCommit: (target, deltaDays) => {
      if (deltaDays === 0) return;
      const item = data.allRows().find((row) => row.id === target.itemId);
      if (item === undefined) return;
      const next = dragValue({
        current: item.values[target.columnId],
        role: target.role,
        deltaDays,
        shift: addDays,
      });
      if (next === null) return; // C3: the clamp writes nothing.
      write(target.itemId, target.columnId, next);
    },
  });

  /**
   * **P-141.** Drawing a dependency. The chart could read one (the elbow arrow and the
   * successor's `data-dependency`) and never create one: a bar carried two resize handles and
   * nothing else, so linking two items meant leaving the gantt for the board table's
   * `dependency` cell editor.
   *
   * A link is one ordinary **cell write** on the successor's `dependency` column — the same
   * writer the bar drag uses — so the activity row, the events outbox, automations and realtime
   * all come for free and no gantt-side server code exists (`CLAUDE.md` §5). Which end monday
   * writes is q528; the conservative reading is the one the arrow already draws, predecessor's
   * right edge → successor's left edge, so a drag from a bar's **right** port makes the bar the
   * predecessor and a drag from its **left** port makes it the successor.
   */
  const toggleDependency = useCallback(
    (fromItemId: string, role: DependencyPortRole, toItemId: string) => {
      if (dependencyColumn === undefined || fromItemId === toItemId) return;
      const successorId = role === 'port-right' ? toItemId : fromItemId;
      const predecessorId = role === 'port-right' ? fromItemId : toItemId;
      const successor = data.allRows().find((row) => row.id === successorId);
      if (successor === undefined) return;
      const current = dependsOnOf(successor.values[dependencyColumn.id]);
      /*
       * The same gesture **removes** the link it would otherwise draw, which is how a dependency
       * is deleted without putting a second hit target on the chart: the elbow runs within a few
       * pixels of both bars' resize handles, and an affordance that steals a handle's pixels
       * would break the drag the chart is for. monday's own removal gesture is unrecorded
       * (q528); a toggle is the conservative reading of "draw and remove", and the cell write is
       * identical either way.
       */
      const next = current.includes(predecessorId)
        ? current.filter((id) => id !== predecessorId)
        : [...current, predecessorId];
      write(successorId, dependencyColumn.id, { item_ids: next.map(Number) });
    },
    [dependencyColumn, data, write],
  );

  const link = useDependencyDrag({ onLink: toggleDependency });

  /**
   * **P-033.** The opening viewport is **today**, centred, whatever the data's range — measured
   * on monday's own Gantt for this board (`174316936` / `1756489`): it opens on the current week
   * with the today line through it, while ours opened on 2019, the earliest bar, seven years
   * from the work. `Auto Fit` is the opt-in that shows everything, and the first bar is only
   * the fallback for a board with no day in range at all.
   */
  const openAtDay = today >= axis.from && today <= axis.to ? today : null;
  const firstBarDay = useMemo(() => {
    const days = groups.flatMap((group) => group.bars.map((bar) => bar.from));
    return days.length === 0 ? null : days.reduce((earliest, day) => (day < earliest ? day : earliest));
  }, [groups]);

  useEffect(() => {
    const element = bodyRef.current;
    const day = openAtDay ?? firstBarDay;
    if (element === null || day === null) return;
    const key = `${effectiveZoom}:${axis.from}:${axis.to}:${day}`;
    if (scrolledTo.current === key) return;
    scrolledTo.current = key;
    const centred = axis.x(day) - Math.max(0, viewportWidth / 2);
    element.scrollLeft = Math.max(0, openAtDay === null ? axis.x(day) - 24 : centred);
  }, [axis, effectiveZoom, firstBarDay, openAtDay, viewportWidth]);

  const fields = useSettingsFields(settings, handle.save, columns);

  if (!handle.ready || (data.pending && data.payload === null)) return <PageSkeleton variant="board" />;
  if (data.error !== null) return <PageEmptyState title="This view could not be loaded" hint={data.error.message} />;

  const totalHeight = rows.reduce((sum, row) => sum + row.height, 0);
  const todayX = today >= axis.from && today <= axis.to ? axis.x(today) : null;
  const weekendBands = settings.showWeekends
    ? axis.minor().filter((cell) => cell.weekend)
    : [];

  return (
    <div
      className="ez-tl"
      data-testid={`${kind}-view`}
      data-zoom={effectiveZoom}
      style={{ ['--ez-tl-side' as string]: `${chrome.sidePanelWidth}px` }}
    >
      {/*
        P-025: the view's own controls are now `extras` inside the shared board toolbar, so the
        timeline wears monday's whole bar — `Timeline ▾ · New item ▾ · Search · Person · Filter`
        — with Baseline, the today button, Auto Fit and the zoom stepper after it, in monday's
        own order. The `ez-tl__toolbar` wrapper stays as the extras' own flex row.
      */}
      <BoardViewToolbar
        controls={toolbar.controls}
        boardId={boardId}
        viewId={viewId}
        registryId={kind}
        source={source}
        payload={data.payload}
        onChanged={() => data.refresh()}
        gear={<ViewToolbar title={kind === 'timeline' ? 'Timeline' : 'Gantt'} fields={fields} testId={`${kind}-settings`} />}
        extras={
      <div className="ez-tl__toolbar" data-testid={`${kind}-toolbar`}>
        <button
          type="button"
          className="ez-tl__button ez-tl__button--icon"
          data-testid="timeline-today"
          aria-label="Today"
          onClick={() => {
            const element = bodyRef.current;
            if (element !== null && todayX !== null) element.scrollLeft = Math.max(0, todayX - viewportWidth / 2);
          }}
        >
          ◎
        </button>
        <button
          type="button"
          className="ez-tl__button"
          data-testid="timeline-fit"
          onClick={() => pickZoom(fitZoom(range, viewportWidth))}
        >
          Auto Fit
        </button>
        {chrome.showBaseline ? (
          <button type="button" className="ez-tl__button" data-testid="gantt-baseline" disabled title="Baselines are out of scope for M4.V3 (spec §3)">
            Baseline
          </button>
        ) : null}
        <div className="ez-tl__zoom" data-testid="timeline-zoom">
          <select
            aria-label="Zoom"
            value={effectiveZoom}
            onChange={(event) => pickZoom(event.target.value as TimelineZoom)}
          >
            {TIMELINE_ZOOMS.map((option) => (
              <option key={option} value={option}>
                {option[0]!.toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ez-tl__zoom-step"
            aria-label="Zoom out"
            disabled={effectiveZoom === 'years'}
            onClick={() => pickZoom(stepZoom(effectiveZoom, 1))}
          >
            −
          </button>
          <button
            type="button"
            className="ez-tl__zoom-step"
            aria-label="Zoom in"
            disabled={effectiveZoom === 'days'}
            onClick={() => pickZoom(stepZoom(effectiveZoom, -1))}
          >
            +
          </button>
        </div>
      </div>
        }
      />

      {error !== null ? <div className="ez-tl__error" role="alert">{error}</div> : null}

      <div className="ez-tl__body" ref={bodyRef} data-testid={`${kind}-body`}>
        <div className="ez-tl__side" style={{ width: chrome.sidePanelWidth }} data-testid="timeline-side-panel">
          <div className="ez-tl__header">
            <div className="ez-tl__header-row ez-tl__header-row--major" />
            <div className="ez-tl__header-row ez-tl__header-row--minor" />
          </div>
          {rows.map((row) =>
            row.kind === 'group' ? (
              <button
                key={`g:${row.group.id}`}
                type="button"
                className="ez-tl__group-head"
                data-testid="timeline-group-head"
                data-group-id={row.group.id}
                onClick={() =>
                  handle.save({
                    collapsedGroupKeys: row.group.collapsed
                      ? settings.collapsedGroupKeys.filter((key) => key !== row.group.id)
                      : [...settings.collapsedGroupKeys, row.group.id],
                  })
                }
              >
                <span className="ez-tl__group-dot" style={{ background: row.group.color }} />
                <span>{row.group.title === '' ? `${row.group.bars.length} items` : row.group.title}</span>
              </button>
            ) : (
              <div key={`s:${row.bar.itemId}`} className="ez-tl__side-row" data-testid="timeline-side-row" data-item-id={row.bar.itemId}>
                <span className="ez-tl__side-name">{row.bar.name}</span>
                {chrome.sidePanelDates ? (
                  <span className="ez-tl__side-dates">
                    {row.bar.milestone ? row.bar.from : `${row.bar.from} – ${row.bar.to}`}
                  </span>
                ) : null}
              </div>
            ),
          )}
        </div>

        <div className="ez-tl__canvas" style={{ width: axis.width }} data-testid="timeline-canvas">
          <div className="ez-tl__header">
            <div className="ez-tl__header-row ez-tl__header-row--major">
              {axis.major().map((cell) => (
                <div key={cell.key} className="ez-tl__cell ez-tl__cell--major" style={{ left: cell.x, width: cell.width }}>
                  {/* A month cell is 9,982 px wide at Days zoom; monday keeps its label on
                      screen (the `2017` in `prod-gantt` sits at x 22 of a 365 px cell that
                      starts off-screen), so the label sticks to the visible edge. */}
                  <span className="ez-tl__cell-label">{cell.label}</span>
                </div>
              ))}
            </div>
            <div className="ez-tl__header-row ez-tl__header-row--minor">
              {axis.minor().map((cell) => (
                <div
                  key={cell.key}
                  className={`ez-tl__cell ez-tl__cell--minor${cell.weekend && settings.showWeekends ? ' ez-tl__cell--weekend' : ''}`}
                  style={{ left: cell.x, width: cell.width }}
                  data-testid="timeline-minor-cell"
                  data-day={cell.from}
                  /* P-033: monday paints the cell today falls in — `W37 7 – 13` — as a blue pill. */
                  data-today={today >= cell.from && today <= cell.to ? 'true' : undefined}
                  data-weekend={cell.weekend && settings.showWeekends ? 'true' : 'false'}
                >
                  {effectiveZoom === 'days' ? <span className="ez-tl__day-name">{dayNameOf(cell.from)}</span> : null}
                  <span>{cell.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="ez-tl__rows" ref={rowsRef} style={{ height: totalHeight }} data-testid="timeline-rows">
            {weekendBands.map((cell) => (
              <div key={`w:${cell.key}`} className="ez-tl__weekend-band" style={{ left: cell.x, width: cell.width }} />
            ))}
            {settings.showTodayLine && todayX !== null ? (
              <div className="ez-tl__today" style={{ left: todayX }} data-testid="timeline-today-line" data-x={todayX} />
            ) : null}

            {rows.map((row, index) => {
              const top = rows.slice(0, index).reduce((sum, entry) => sum + entry.height, 0);
              if (row.kind === 'group') {
                const rollup = settings.showRollup && row.group.rollup !== null ? axis.bar(row.group.rollup.from, row.group.rollup.to) : null;
                return (
                  <div key={`gl:${row.group.id}`} className="ez-tl__lane ez-tl__lane--group" style={{ position: 'absolute', top, left: 0, right: 0 }}>
                    {rollup !== null ? (
                      <div
                        className="ez-tl__rollup"
                        style={{ left: rollup.x, width: rollup.width, background: `color-mix(in srgb, ${row.group.color} 18%, transparent)` }}
                        data-testid="timeline-rollup"
                        data-group-id={row.group.id}
                      >
                        <span className="ez-tl__rollup-title">{row.group.title}</span>
                        <span className="ez-tl__rollup-progress" style={{ width: rollup.width, background: row.group.color }} />
                      </div>
                    ) : null}
                  </div>
                );
              }
              const box = axis.bar(row.bar.from, row.bar.to);
              return (
                <div key={`bl:${row.bar.itemId}`} className="ez-tl__lane ez-tl__lane--row" style={{ position: 'absolute', top, left: 0, right: 0 }}>
                  {row.bar.milestone ? (
                    <div
                      className="ez-tl__milestone"
                      style={{ left: box.x, background: row.bar.color }}
                      data-testid="gantt-bar"
                      data-item-id={row.bar.itemId}
                      data-role="milestone"
                      data-dependency={row.bar.dependsOn.length > 0 ? 'true' : 'false'}
                      onClick={() => {
                        if (drag.consumeClick() || link.consumeClick()) return;
                        navigate(`/boards/${boardId}/views/${viewId}/pulses/${row.bar.itemId}`);
                      }}
                      onPointerDown={(event) => drag.start(event, { itemId: row.bar.itemId, columnId: row.bar.columnId, role: 'bar' })}
                    />
                  ) : (
                    <div
                      className="ez-tl__bar"
                      style={{ left: box.x, width: box.width }}
                      data-testid="gantt-bar"
                      data-item-id={row.bar.itemId}
                      data-role="bar"
                      data-from={row.bar.from}
                      data-to={row.bar.to}
                      data-dependency={row.bar.dependsOn.length > 0 ? 'true' : 'false'}
                      onClick={() => {
                        if (drag.consumeClick() || link.consumeClick()) return;
                        navigate(`/boards/${boardId}/views/${viewId}/pulses/${row.bar.itemId}`);
                      }}
                      onPointerDown={(event) => drag.start(event, { itemId: row.bar.itemId, columnId: row.bar.columnId, role: 'bar' })}
                    >
                      <span className="ez-tl__bar-fill" style={{ width: box.width, background: row.bar.color }} />
                      <span
                        className="ez-tl__handle ez-tl__handle--left"
                        style={{ left: -GANTT_METRICS.handleWidth }}
                        data-testid="gantt-handle"
                        data-item-id={row.bar.itemId}
                        data-role="handle-left"
                        onPointerDown={(event) => drag.start(event, { itemId: row.bar.itemId, columnId: row.bar.columnId, role: 'handle-left' })}
                      >
                        <span className="ez-tl__handle-grip" />
                      </span>
                      <span
                        className="ez-tl__handle ez-tl__handle--right"
                        style={{ left: box.width }}
                        data-testid="gantt-handle"
                        data-item-id={row.bar.itemId}
                        data-role="handle-right"
                        onPointerDown={(event) => drag.start(event, { itemId: row.bar.itemId, columnId: row.bar.columnId, role: 'handle-right' })}
                      >
                        <span className="ez-tl__handle-grip" />
                      </span>
                      {/*
                        P-141: the link ports. Two per bar, one at each end, and only when the
                        board has a `dependency` column to write. `stopPropagation` in the drag's
                        own `start` is what keeps a port press off the bar drag and off the click
                        that opens the item card.
                      */}
                      {dependencyColumn === undefined ? null : (
                        <>
                          <span
                            className="ez-tl__port ez-tl__port--left"
                            data-testid="gantt-dependency-port"
                            data-item-id={row.bar.itemId}
                            data-role="port-left"
                            role="button"
                            tabIndex={-1}
                            aria-label={`Link ${row.bar.name} to a predecessor`}
                            title="Drag to another bar to link them, or to a linked one to unlink"
                            onPointerDown={(event) => link.start(event, row.bar.itemId, 'port-left')}
                          />
                          <span
                            className="ez-tl__port ez-tl__port--right"
                            style={{ left: box.width }}
                            data-testid="gantt-dependency-port"
                            data-item-id={row.bar.itemId}
                            data-role="port-right"
                            role="button"
                            tabIndex={-1}
                            aria-label={`Link ${row.bar.name} to a successor`}
                            title="Drag to another bar to link them, or to a linked one to unlink"
                            onPointerDown={(event) => link.start(event, row.bar.itemId, 'port-right')}
                          />
                        </>
                      )}
                      <span className="ez-tl__bar-label" style={{ left: box.width + GANTT_METRICS.handleWidth }}>
                        {row.bar.label}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {edges.length > 0 ? (
              <svg className="ez-tl__arrows" width={axis.width} height={totalHeight} data-testid="timeline-arrows">
                {edges.map((edge) => {
                  const from = rows.find((row) => row.kind === 'bar' && row.bar.itemId === edge.fromItemId);
                  const to = rows.find((row) => row.kind === 'bar' && row.bar.itemId === edge.toItemId);
                  if (from?.kind !== 'bar' || to?.kind !== 'bar') return null;
                  const fromBox = axis.bar(from.bar.from, from.bar.to);
                  const toBox = axis.bar(to.bar.from, to.bar.to);
                  const y1 = (tops.get(edge.fromItemId) ?? 0) + GANTT_METRICS.rowHeight / 2;
                  const y2 = (tops.get(edge.toItemId) ?? 0) + GANTT_METRICS.rowHeight / 2;
                  const x1 = fromBox.x + fromBox.width;
                  const x2 = toBox.x;
                  const stub = 6;
                  const points = `${x1},${y1} ${x1 + stub},${y1} ${x1 + stub},${y2} ${x2 - 5},${y2}`;
                  return (
                    <g key={`${edge.fromItemId}>${edge.toItemId}`} data-testid="timeline-arrow" data-from={edge.fromItemId} data-to={edge.toItemId}>
                      <polyline className="ez-tl__arrow" points={points} />
                      <polygon className="ez-tl__arrow-head" points={`${x2},${y2} ${x2 - 5},${y2 - 3} ${x2 - 5},${y2 + 3}`} />
                    </g>
                  );
                })}
              </svg>
            ) : null}

            {/* P-141: the link being drawn — a dashed elbow from the port to the pointer. */}
            {link.state === null || rowsRef.current === null ? null : (
              <svg className="ez-tl__arrows" width={axis.width} height={totalHeight} data-testid="timeline-link-preview">
                <line
                  className="ez-tl__link-line"
                  x1={link.state.from.x - rowsRef.current.getBoundingClientRect().left}
                  y1={link.state.from.y - rowsRef.current.getBoundingClientRect().top}
                  x2={link.state.point.x - rowsRef.current.getBoundingClientRect().left}
                  y2={link.state.point.y - rowsRef.current.getBoundingClientRect().top}
                />
              </svg>
            )}
          </div>
        </div>
      </div>

      {settings.displayLegend ? (
        <div className="ez-tl__legend" data-testid="timeline-legend">
          {(data.payload?.groups ?? []).map((group) => (
            <span key={group.id} className="ez-tl__legend-field">
              <span className="ez-tl__legend-dot" style={{ background: group.color }} />
              {group.title}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function stepZoom(zoom: TimelineZoom, delta: number): TimelineZoom {
  const index = TIMELINE_ZOOMS.indexOf(zoom);
  const next = Math.min(Math.max(index + delta, 0), TIMELINE_ZOOMS.length - 1);
  return TIMELINE_ZOOMS[next] as TimelineZoom;
}

/** monday's *Visual settings* group plus the three axis pickers (`[V3§2.4]`). */
function useSettingsFields(
  settings: GanttViewSettings,
  save: (patch: Partial<GanttViewSettings>) => void,
  columns: readonly { id: string; title: string; type: string }[],
): SettingsField[] {
  return useMemo(() => {
    const dateColumns = dateColumnsOf(columns as never).map((column) => ({ id: column.id, title: column.title, type: column.type }));
    const axisOptions = [
      { value: 'none', label: 'None' },
      { value: 'group', label: 'Group' },
      ...columns.map((column) => ({ value: column.id, label: column.title })),
    ];
    return [
      {
        kind: 'columns',
        id: 'columns',
        label: 'Timeline column',
        columns: dateColumns,
        selection: settings.dateColumns,
        // P-146: `barColumnFor` places by **every** date-ish column when nothing is chosen.
        emptyMeans: 'all',
        onChange: (dateColumnsNext) => save({ dateColumns: dateColumnsNext }),
      },
      {
        kind: 'select',
        id: 'group_by_id',
        label: 'Group by',
        value: settings.groupBy ?? 'none',
        options: axisOptions,
        onChange: (value) => save({ groupBy: value }),
      },
      {
        kind: 'select',
        id: 'label_by_id',
        label: 'Label by',
        value: settings.labelBy ?? 'name',
        options: [{ value: 'name', label: 'Name' }, ...columns.map((column) => ({ value: column.id, label: column.title }))],
        onChange: (value) => save({ labelBy: value }),
      },
      {
        kind: 'select',
        id: 'color_by_id',
        label: 'Color by',
        value: settings.colorBy ?? 'group',
        options: axisOptions,
        onChange: (value) => save({ colorBy: value }),
      },
      { kind: 'toggle', id: 'enable_visual_dependencies', label: 'Show dependencies', value: settings.showDependencies, onChange: (value) => save({ showDependencies: value }) },
      { kind: 'toggle', id: 'show_rollup', label: 'Show group summary', value: settings.showRollup, onChange: (value) => save({ showRollup: value }) },
      { kind: 'toggle', id: 'show_today_line', label: 'Show Today Indication', value: settings.showTodayLine, onChange: (value) => save({ showTodayLine: value }) },
      { kind: 'toggle', id: 'show_weekends', label: 'Show weekend', value: settings.showWeekends, onChange: (value) => save({ showWeekends: value }) },
      { kind: 'toggle', id: 'display_legend', label: 'Show color legend', value: settings.displayLegend, onChange: (value) => save({ displayLegend: value }) },
    ];
  }, [settings, save, columns]);
}

interface DragTarget {
  itemId: string;
  columnId: string;
  role: 'bar' | 'handle-left' | 'handle-right';
}

export type DependencyPortRole = 'port-left' | 'port-right';

/**
 * **P-141.** The link gesture: press a bar's port, drag across the chart, release over another
 * bar. It is deliberately *not* `useTimelineDrag` — that one snaps a pixel delta to whole days
 * and commits a date; this one only has to know which bar the pointer was released over, which
 * is `elementFromPoint` and nothing else. The listeners are on `window`, so a drag that leaves
 * the bar (which every link does, by definition) is still tracked.
 */
function useDependencyDrag(input: {
  onLink(fromItemId: string, role: DependencyPortRole, toItemId: string): void;
}) {
  const [state, setState] = useState<{
    fromItemId: string;
    role: DependencyPortRole;
    from: { x: number; y: number };
    point: { x: number; y: number };
  } | null>(null);
  /** A link drag ends over a bar, whose `click` must not also open the item card. */
  const dragged = useRef(false);

  const start = useCallback(
    (event: React.PointerEvent, fromItemId: string, role: DependencyPortRole) => {
      // The port lives inside the bar: without this the bar's own drag starts too.
      event.stopPropagation();
      event.preventDefault();
      const from = { x: event.clientX, y: event.clientY };
      dragged.current = false;
      setState({ fromItemId, role, from, point: from });

      const move = (moveEvent: PointerEvent) => {
        if (Math.abs(moveEvent.clientX - from.x) > 4 || Math.abs(moveEvent.clientY - from.y) > 4) {
          dragged.current = true;
        }
        setState((current) =>
          current === null ? null : { ...current, point: { x: moveEvent.clientX, y: moveEvent.clientY } },
        );
      };
      const finish = (upEvent: PointerEvent) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', finish);
        window.removeEventListener('pointercancel', cancel);
        setState(null);
        const under = document.elementFromPoint(upEvent.clientX, upEvent.clientY);
        const bar = under?.closest('[data-testid="gantt-bar"]') ?? null;
        const toItemId = bar?.getAttribute('data-item-id') ?? null;
        if (toItemId !== null) input.onLink(fromItemId, role, toItemId);
      };
      const cancel = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', finish);
        window.removeEventListener('pointercancel', cancel);
        setState(null);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', finish);
      window.addEventListener('pointercancel', cancel);
    },
    [input],
  );

  const consumeClick = useCallback((): boolean => {
    if (!dragged.current) return false;
    dragged.current = false;
    return true;
  }, []);

  return { start, state, consumeClick };
}

/**
 * Pointer-capture drag, snapped to whole days through the axis's own scale (`[V3§6]`, C5).
 * monday's timeline column has no sub-day precision, so a pixel delta becomes a day delta here
 * and nowhere else.
 */
function useTimelineDrag(input: {
  pxPerDay: number;
  onCommit(target: DragTarget, deltaDays: number): void;
}) {
  const state = useRef<{ target: DragTarget; startX: number } | null>(null);
  /**
   * A drag must not *also* open the item card. `pointerup` runs before `click`, so the commit
   * sets this and the bar's `onClick` consumes it — without it, every drag left the item card
   * covering the board and the next gesture hit the card instead (caught by
   * `views-timeline.spec.ts` C2, not by reading the code).
   */
  const dragged = useRef(false);

  const start = useCallback((event: React.PointerEvent, target: DragTarget) => {
    // A click that opens the item card must still work: a drag only counts on a real move.
    state.current = { target, startX: event.clientX };
    dragged.current = false;
    const element = event.currentTarget as HTMLElement;
    element.setPointerCapture?.(event.pointerId);
    const finish = (upEvent: PointerEvent) => {
      element.removeEventListener('pointerup', finish);
      element.removeEventListener('pointercancel', cancel);
      const current = state.current;
      state.current = null;
      if (current === null) return;
      const moved = Math.abs(upEvent.clientX - current.startX);
      const deltaDays = Math.round((upEvent.clientX - current.startX) / input.pxPerDay);
      // 4 px of slop: a click wobbles, and a wobble must not be a write.
      if (moved > 4) {
        dragged.current = true;
        upEvent.preventDefault();
        upEvent.stopPropagation();
      }
      if (deltaDays !== 0) input.onCommit(current.target, deltaDays);
    };
    const cancel = () => {
      element.removeEventListener('pointerup', finish);
      element.removeEventListener('pointercancel', cancel);
      state.current = null;
    };
    element.addEventListener('pointerup', finish);
    element.addEventListener('pointercancel', cancel);
    event.stopPropagation();
  }, [input]);

  /** `true` once, for the click that a drag's `pointerup` is about to produce. */
  const consumeClick = useCallback((): boolean => {
    if (!dragged.current) return false;
    dragged.current = false;
    return true;
  }, []);

  return { start, consumeClick };
}
